import { useRef, useState } from 'react'
import { ArrowUp, Bookmark, ChevronRight, Clock, TriangleAlert, Users } from 'lucide-react'
import type { AppState, ChatMessage, Recipe, StageKey } from '../../shared/types.ts'
import { api, streamChat } from '../lib/api.ts'
import { cx } from '../lib/util.ts'
import { AltSkeleton, HeroSkeleton } from './MenuSkeleton.tsx'
import { DishPhoto } from './DishPhoto.tsx'
import { RecipeTags } from './RecipeTags.tsx'

const OPENERS = [
  "what's quick with what I've got?",
  'something comforting, minimal washing up',
  'high protein, under 30 minutes',
  'use up what is about to go off',
]

const STROKE = 2.6

/** Long enough that a quick answer never flashes an empty frame on its way in. */
const SKELETON_AFTER = 1800

interface Props {
  state: AppState
  onState: (patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)) => void
  keysReady: boolean
  onOpenRecipe: (r: Recipe) => void
}

/**
 * Tonight's menu: the newest result set as an editorial spread — one hero pick,
 * then numbered alternates. Earlier turns stay in `state.conversation`; this screen
 * deliberately shows only the latest, with the bar below it to ask again.
 */
export function CookMenu({ state, onState, keysReady, onOpenRecipe }: Props) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [stage, setStage] = useState<string>('')
  const [error, setError] = useState('')
  /** What has arrived so far this turn, shown until the finished message replaces it. */
  const [live, setLive] = useState<{ ask: string; reply?: string; recipes: Recipe[] } | null>(null)
  const [skeleton, setSkeleton] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const skeletonTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const savedIds = new Set(state.saved.map((s) => s.recipe.id))
  const latest = lastWithRecipes(state.conversation)
  /*
   * While a new turn is still generating, the previous menu stays up rather than
   * blanking — but the heading has to move with it, or the kicker claims you asked
   * for one thing while the cards below are still answering the last one.
   */
  /*
   * Once the frame is up the screen belongs to the new ask: heading, cards and blanks
   * all describe the turn being written. Before that the previous menu stays whole,
   * heading included, so a fast answer never blinks the page away for nothing.
   */
  const answering = Boolean(live) && (skeleton || Boolean(live?.recipes.length))
  const recipes = answering ? live!.recipes : latest?.recipes ?? []
  const [hero, ...alts] = recipes
  const ask = answering ? live!.ask : lastAsk(state.conversation)
  const pending = answering ? Math.max(0, Math.min(5, Math.round(state.settings.recipeCount)) - recipes.length) : 0

  const send = async (text: string) => {
    const body = text.trim()
    if (!body || busy) return
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setDraft('')
    setBusy(true)
    setStage('Searching for recipes')
    setError('')
    setLive({ ask: body, recipes: [] })
    setSkeleton(false)
    if (skeletonTimer.current) clearTimeout(skeletonTimer.current)
    skeletonTimer.current = setTimeout(() => setSkeleton(true), SKELETON_AFTER)
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })

    try {
      await streamChat(body, (event) => {
        if (event.type === 'stage' && event.status === 'running') setStage(event.label)
        else if (event.type === 'partial') {
          setLive((l) => (l ? { ...l, reply: event.reply ?? l.reply, recipes: event.recipes } : l))
        } else if (event.type === 'message') {
          setLive(null)
          onState((s) => ({ conversation: [...s.conversation, mkUser(body), event.message] }))
          if (event.message.failed) setError(event.message.text)
        } else if (event.type === 'photos') {
          // Arrives after the message, so it folds into whatever is there by then.
          onState((s) => ({
            conversation: s.conversation.map((m) =>
              m.recipes ? { ...m, recipes: m.recipes.map((r) => (event.photos[r.id] ? { ...r, photo: event.photos[r.id] } : r)) } : m,
            ),
          }))
        } else if (event.type === 'error') setError(event.message)
      }, controller.signal)
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setError((e as Error).message)
    } finally {
      if (skeletonTimer.current) clearTimeout(skeletonTimer.current)
      setBusy(false)
      setStage('')
      setLive(null)
      setSkeleton(false)
      abortRef.current = null
    }
  }

  const toggleSave = async (recipe: Recipe) => {
    const res = savedIds.has(recipe.id)
      ? await api.unsaveRecipe(recipe.id)
      : await api.saveRecipe(recipe, ask)
    onState({ saved: res.saved })
  }

  return (
    <div className="menu-screen">
      <div className="menu-scroll noscroll" ref={scrollRef}>
        {busy && !hero && !skeleton && <div className="thinking"><span className="spinner" />{stage}…</div>}

        {answering && !hero && (
          <>
            <div className="kicker">Tonight · you asked to</div>
            <h2 className="ask">{ask}</h2>
            <HeroSkeleton />
          </>
        )}

        {!busy && !hero && (
          <div className="empty-cook">
            <h2>What are you making?</h2>
            <p>I know your kitchen, what you avoid and how you feel about shopping. Tell me what you feel like.</p>
            <div className="openers">
              {OPENERS.map((o) => (
                <button key={o} className="pick" onClick={() => void send(o)} disabled={!keysReady}>{o}</button>
              ))}
            </div>
          </div>
        )}

        {hero && (
          <>
            <div className="kicker">Tonight · you asked to</div>
            <h2 className="ask">{ask}</h2>

            <article className="hero">
              <div className="hero-photo">
                <DishPhoto src={hero.photo} alt="" className="fill" />
                {hero.fitScore != null && <span className="fit-seal">{hero.fitScore}</span>}
              </div>
              <div className="hero-body">
                <RecipeTags cuisine={hero.cuisine} tags={hero.tags} />
                <h3 className="hero-title">{hero.title}</h3>
                {hero.blurb && <p className="hero-blurb">{hero.blurb}</p>}
                <div className="metarow">
                  <span><Clock size={14} strokeWidth={STROKE} />{hero.totalMinutes} min</span>
                  <span><Users size={14} strokeWidth={STROKE} />serves {hero.servings}</span>
                </div>
                {hero.warnings?.[0] && (
                  <div className="warn-strip">
                    <TriangleAlert size={15} strokeWidth={STROKE} />
                    <span>{hero.warnings[0]}</span>
                  </div>
                )}
                <div className="hero-actions">
                  <button className="btn btn-primary" onClick={() => onOpenRecipe(hero)}>Open recipe</button>
                  <button
                    className={cx('btn btn-secondary save', savedIds.has(hero.id) && 'on')}
                    aria-label={savedIds.has(hero.id) ? 'Remove from saved' : 'Save recipe'}
                    onClick={() => void toggleSave(hero)}
                  >
                    <Bookmark size={19} strokeWidth={STROKE} fill={savedIds.has(hero.id) ? 'currentColor' : 'none'} />
                  </button>
                </div>
              </div>
            </article>

            {(alts.length > 0 || pending > 0) && (
              <>
                <div className="rule"><span>Also tonight</span></div>
                <div className="alts">
                  {alts.map((r, i) => (
                    <button key={r.id} className="alt" onClick={() => onOpenRecipe(r)}>
                      <DishPhoto src={r.photo} alt="" className="thumb" />
                      <span className="alt-n">{i + 2}</span>
                      <span className="alt-main">
                        <span className="alt-title">{r.title}</span>
                        <span className="alt-meta">{r.totalMinutes} min · serves {r.servings}</span>
                      </span>
                      <ChevronRight size={20} strokeWidth={STROKE} className="chev" />
                    </button>
                  ))}
                  {Array.from({ length: pending }, (_, i) => <AltSkeleton key={`skel-${i}`} />)}
                </div>
              </>
            )}
          </>
        )}

        {answering && !hero && pending > 1 && (
          <>
            <div className="rule"><span>Also tonight</span></div>
            <div className="alts">
              {Array.from({ length: pending - 1 }, (_, i) => <AltSkeleton key={`skel-alt-${i}`} />)}
            </div>
          </>
        )}

        {error && <div className="notice error" style={{ marginTop: 16 }}>{error}</div>}
        {!keysReady && (
          <div className="notice info" style={{ marginTop: 16 }}>
            Add <code>PERPLEXITY_API_KEY</code> to <code>.env</code> and restart the server.
          </div>
        )}
      </div>

      <div className="refine">
        <div className="refine-row">
          <input
            value={draft}
            placeholder={hero ? 'not quite? ask again…' : 'chicken thighs, half a cabbage, 25 minutes…'}
            disabled={!keysReady}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void send(draft) }}
          />
          <button className="send" aria-label="Send" disabled={!keysReady || busy || !draft.trim()} onClick={() => void send(draft)}>
            {busy ? <span className="spinner" /> : <ArrowUp size={19} strokeWidth={STROKE} />}
          </button>
        </div>
      </div>
    </div>
  )
}

const lastWithRecipes = (c: ChatMessage[]): ChatMessage | undefined =>
  [...c].reverse().find((m) => m.recipes && m.recipes.length > 0)

/** The ask that produced the newest menu — the user turn before it. */
function lastAsk(conversation: ChatMessage[]): string {
  const at = conversation.map((m) => Boolean(m.recipes?.length)).lastIndexOf(true)
  for (let i = at; i >= 0; i--) if (conversation[i].role === 'user') return conversation[i].text
  return ''
}

let seq = 0
const mkUser = (text: string): ChatMessage =>
  ({ id: `local-u${seq++}`, role: 'user', text, at: new Date().toISOString() })
